import { Box, Card, Typography } from "@mui/material";
import { useState, useCallback, useMemo } from "react";
import { OverlayVsStockEditModal } from "./OverlayVsStockEditModal";
import { overlayVsStockChartStyles } from "../../../styles/OverlayVsStockChart.styles";

const DEFAULT_CHART_LIMIT = 100;

interface OverlayVsStockChartProps {
  stockOnlyValue: number;
  stockOverlayValue: number;
  limit?: number;
  onLimitChange?: (limit: number) => void;
}

const formatPercentage = (value: number): string => {
  return `${value.toFixed(2)}%`;
};

export const OverlayVsStockChart = ({
  stockOnlyValue,
  stockOverlayValue,
  limit: controlledLimit,
  onLimitChange,
}: OverlayVsStockChartProps) => {
  const [internalLimit, setInternalLimit] = useState(DEFAULT_CHART_LIMIT);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const limit = controlledLimit ?? internalLimit;

  const handleClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      if (onLimitChange) {
        onLimitChange(newLimit);
      } else {
        setInternalLimit(newLimit);
      }
    },
    [onLimitChange],
  );

  const stockOnlyPercentage = useMemo(
    () => Math.min((stockOnlyValue / limit) * 100, 100),
    [stockOnlyValue, limit],
  );

  const stockOverlayPercentage = useMemo(
    () => Math.min((stockOverlayValue / limit) * 100, 100),
    [stockOverlayValue, limit],
  );

  const stockOnlyFilledWidth = useMemo(
    () => `${stockOnlyPercentage}%`,
    [stockOnlyPercentage],
  );

  const stockOverlayFilledWidth = useMemo(
    () => `${stockOverlayPercentage}%`,
    [stockOverlayPercentage],
  );

  return (
    <>
      <Card
        sx={overlayVsStockChartStyles.card}
        onClick={handleClick}
        tabIndex={0}
        role="button"
        aria-label="Click to edit chart limits"
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleClick();
          }
        }}
      >
        <Typography variant="h6" sx={overlayVsStockChartStyles.title}>
          Overlay vs Stock Only{" "}
          <Typography
            component="span"
            sx={overlayVsStockChartStyles.titleIllustrative}
          >
            (Illustrative)
          </Typography>
        </Typography>

        <Box sx={overlayVsStockChartStyles.barsContainer}>
          <Box sx={overlayVsStockChartStyles.barRow}>
            <Typography sx={overlayVsStockChartStyles.barLabel}>
              Stock Only
            </Typography>
            <Box sx={overlayVsStockChartStyles.barContainer}>
              <Box
                sx={{
                  ...overlayVsStockChartStyles.barStockOnlyFilled,
                  width: stockOnlyFilledWidth,
                }}
              />
            </Box>
            <Typography sx={overlayVsStockChartStyles.barValue}>
              {formatPercentage(stockOnlyValue)}
            </Typography>
          </Box>

          <Box
            sx={{
              ...overlayVsStockChartStyles.barRow,
              marginBottom: 0,
            }}
          >
            <Typography sx={overlayVsStockChartStyles.barLabel}>
              Stock + Overlay
            </Typography>
            <Box sx={overlayVsStockChartStyles.barContainer}>
              <Box
                sx={{
                  ...overlayVsStockChartStyles.barStockOverlayFilled,
                  width: stockOverlayFilledWidth,
                }}
              />
            </Box>
            <Typography sx={overlayVsStockChartStyles.barValue}>
              {formatPercentage(stockOverlayValue)}
            </Typography>
          </Box>
        </Box>
      </Card>

      <OverlayVsStockEditModal
        isOpened={isModalOpen}
        onClose={handleCloseModal}
        limit={limit}
        onLimitChange={handleLimitChange}
      />
    </>
  );
};
